import axios from "axios"

export const API_ROUTE = window.location.origin.includes("localhost")
    ? "http://localhost/api"
    : "https://dtkh4xur60uc5.cloudfront.net/api"


export const toasted = (id?: string | undefined, delay?: number) => {
    const obj: any = {
        style: {
            background: "#222",
            color: "#fff",
            padding: "1rem 2rem",
            borderRadius: "100rem",
        },
    }
    if (id) obj["id"] = id
    if (delay) obj["duration"] = delay
    return obj
}

export const getFileColor = (contentType: string) => {
    switch (contentType) {
        case "folder":
            return "#ffb300"
        case "application/pdf":
            return "#FF0000"
        case "text/html":
            return "#6578D8"
        case "application/json":
            return "#5E961F"
        default:
            return "#999"
    }
}

export const exportToDocx = async (content: string, filename: string) => {
    try {
        const response = await axios.post(
            `${API_ROUTE}/utils/convert-html-docx`,
            { content, filename: filename.replace(/\.[^/.]+$/, "") }, // Remove existing extension
            { responseType: "blob" }
        )

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", `${filename}.docx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
    } catch (error) {
        console.error("DOCX export error:", error)
    }
}

export const exportToPdf = (
  htmlString: string,
  filename?: string,
  opts?: {
    /** If true, remove class="" and style="" from all nodes inside the iframe before rendering */
    stripInlineStyling?: boolean
  }
) => {
  const w = window as any

  if (typeof w.html2pdf === "undefined") {
    console.error("html2pdf.js is not loaded. Include it before calling exportToPdf().")
    return
  }

  // Ensure .pdf suffix
  let fname = filename && filename.trim() ? filename.trim() : "document.pdf"
  if (!fname.toLowerCase().endsWith(".pdf")) fname += ".pdf"

  // 1) Create an isolated iframe so **no site CSS** can touch our content.
  const iframe = document.createElement("iframe")
  iframe.style.position = "fixed"
  iframe.style.left = "-99999px"
  iframe.style.top = "0"
  iframe.style.width = "210mm" // not necessary for layout, but fine
  iframe.style.height = "297mm"
  iframe.setAttribute("sandbox", "allow-same-origin") // keep it isolated
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument!
  const win = iframe.contentWindow!

  // 2) Minimal, boring, explicit CSS. White bg, black text. No fancy resets.
  // Also fix width to A4 (~794px @ ~96dpi) so pagination is deterministic.
  const minimalHTML = `
<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  html, body {
    background:#fff !important;
    color:#000 !important;
    margin:0; padding:0;
    font: 14px/1.45 system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
  }
  /* A4 width in px (approx @ 96dpi) for consistent breaking */
  .pdf-root {
    box-sizing: border-box;
    width: 794px;
    margin: 0 auto;
    padding: 24px; /* margin option adds outer margin in PDF, this is inner padding */
    background:#fff !important;
    color:#000 !important;
  }
  /* Basic element sanity */
  img, svg, canvas, video { max-width: 100%; height: auto; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #ddd; padding: 6px; }
  h1,h2,h3,h4,h5,h6 { margin: 0.6em 0 0.3em; }
  p { margin: 0.4em 0; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style>
</head>
<body>
  <div class="pdf-root" id="pdf-root"></div>
</body>
</html>
`.trim()

  doc.open()
  doc.write(minimalHTML)
  doc.close()

  // 3) Inject the caller's HTML into the isolated container.
  const root = doc.getElementById("pdf-root") as HTMLDivElement
  root.innerHTML = htmlString || ""

  // Optional: strip inline styling (kills class/style attrs) if requested
  if (opts?.stripInlineStyling) {
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT)
    const toClean: Element[] = []
    while (walker.nextNode()) toClean.push(walker.currentNode as Element)
    for (const el of toClean) {
      el.removeAttribute("class")
      el.removeAttribute("style")
    }
  }

  // 4) Defensive: ensure computed text is not transparent/white-on-white (site CSS can't reach us, but user HTML might).
  root.style.backgroundColor = "#ffffff"
  root.style.color = "#000000"

  // 5) Wait for images and fonts so html2canvas captures real pixels.
  const whenImagesLoaded = () =>
    Promise.all(
      Array.from(root.querySelectorAll("img"))
        .filter(img => !img.complete)
        .map(img => new Promise<void>(resolve => {
          img.addEventListener("load", () => resolve(), { once: true })
          img.addEventListener("error", () => resolve(), { once: true })
        }))
    )

  const whenFontsReady = (doc as any).fonts && (doc as any).fonts.ready
    ? (doc as any).fonts.ready.catch(() => undefined)
    : Promise.resolve()

  const go = async () => {
    await whenImagesLoaded()
    await whenFontsReady

    const options = {
      margin: 10, // mm
      filename: fname,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        backgroundColor: "#ffffff",    // <-- force non-transparent canvas
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] } // respects page-break-* if present
    }

    try {
      // 6) Render FROM THE IFRAME DOC, not from the main page.
      await w.html2pdf().from(root).set(options).save()
    } catch (err) {
      console.error("exportToPdf failed:", err)
    } finally {
      // cleanup
      setTimeout(() => {
        iframe.remove()
      }, 1000)
    }
  }

  go()
}

export function generateTextFromSpreadsheet(jsonData: any) {
    const sheetData = jsonData?.sheets?.Sheet1?.data?.dataTable || {}
    let textRepresentation = ""

    // Loop through rows and columns to extract cell values
    Object.keys(sheetData).forEach((row) => {
        let rowData = ""
        Object.keys(sheetData[row]).forEach((col) => {
            rowData += `${sheetData[row][col].value} | `
        })
        // Remove the last ' | ' from each row and add a newline character
        rowData = rowData.slice(0, -3)
        textRepresentation += `${rowData}\n`
    })

    return `The current document is an excel spreadsheet. The cells are be separated by pipes "|", analyze sheet row/column values and structure carefully:

${textRepresentation}`
}

export function copyToClipboard(text: string) {
    const el = document.createElement("textarea")
    el.value = text
    document.body.appendChild(el)
    el.select()
    document.execCommand("copy")
    document.body.removeChild(el)
}

export function parseTC(tc: number, format: string = "default"): string {
    const date = new Date(tc * 1000)
    const formats: { [key: string]: Intl.DateTimeFormatOptions } = {
        default: {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        },
    }

    if (!formats[format]) format = "default"
    return date.toLocaleString(undefined, formats[format])
}

export const canTruncate = (text: string, length: number) => {
    if (text?.length > length) return text.slice(0, length) + "..."
    return text
}
